import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, untracked } from '@angular/core';

import { CONTENT_KEYS } from '@core/models/content-keys';
import { ContentService } from '@core/services/content.service';
import { LanguageService } from '@core/services/language.service';
import { PlatformService } from '@core/services/platform.service';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import { type DynamicAction } from '@shared/registry';

import { type WalletButtonConfig, type WalletButtonContent, type WalletType } from './wallet-button.types';

@Component({
  selector: 'app-wallet-button',
  template: `
    <app-groups-renderer
      [groups]="interpolatedGroups()"
      [loadingGroups]="loadingGroups()"
      [isLoading]="isLoading()"
      (componentAction)="onAction()"
    />
  `,
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class WalletButton {
  private readonly platformService = inject(PlatformService);
  private readonly contentService = inject(ContentService<WalletButtonContent>);
  private readonly languageService = inject(LanguageService);

  readonly config = input.required<WalletButtonConfig>();
  readonly action = output<DynamicAction>();

  protected readonly isLoading = computed(() => {
    const contentState = this.contentService.state();
    return this.config().loading || contentState.isLoading() || contentState.isInitial();
  });

  protected readonly walletType = computed((): WalletType => {
    if (this.platformService.isIos()) return 'apple';
    if (this.platformService.isAndroid()) return 'google';
    if (this.platformService.isApplePlatform()) return 'apple';
    return 'google';
  });

  private readonly rawContent = this.contentService.rawContent;

  private readonly jexlContext = computed<Record<string, unknown>>(() => ({
    walletType: this.walletType(),
    ariaLabel: this.config().ariaLabel,
  }));

  private static readonly defaultLoadingGroups: ComponentGroup[] = [
    {
      id: 'wallet-button-loading',
      layout: { direction: 'column', align: 'center', justify: 'center' },
      items: [
        {
          id: 'spinner',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '44px', width: '140px', borderRadius: 'md' } },
        },
      ],
    } as ComponentGroup,
  ];

  protected readonly loadingGroups = computed<ComponentGroup[]>(() => {
    return this.config().loadingGroups ?? WalletButton.defaultLoadingGroups;
  });

  protected readonly interpolatedGroups = computed<ComponentGroup[]>(() => {
    const content = this.rawContent();
    if (!content?.groups) return [];
    return this.contentService.interpolateGroups(content.groups, this.jexlContext());
  });

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      untracked(() => {
        this.contentService
          .loadFeatureContent(CONTENT_KEYS.COMPONENTS._id, CONTENT_KEYS.COMPONENTS.WALLET_BUTTON)
          .catch(console.error);
      });
    });
  }

  protected onAction(): void {
    if (this.config().loading) return;

    const popupWindow = this.platformService.isWeb() ? window.open('about:blank', '_blank') : null;

    const actionToEmit = this.config().action ?? {
      id: 'addToWallet',
      data: {
        walletType: this.walletType(),
        passData: this.config().passData,
        popupWindow,
      },
    };

    this.action.emit(actionToEmit);
  }
}
